import type { DiagramProps } from '@/lib/scad-feature-types';
import { D } from './DiagramStyles';

export function LinearExtrudeDiagram({ values }: DiagramProps) {
  const height = Math.max(1, values.height ?? 10);
  const twist = values.twist ?? 0;
  const scale = Math.max(0.1, values.scale ?? 1);

  const cx = 100;
  const baseY = 125;
  const topY = Math.max(baseY - 90, baseY - Math.min(90, height * 6));

  // 2D hexagonal base profile (simplified as 6-point polygon)
  const hexR = 28;
  const hexPoints = (cy: number, radius: number, rotDeg = 0): string => {
    return Array.from({ length: 6 }, (_, i) => {
      const a = ((i * 60) + rotDeg) * Math.PI / 180;
      return `${(cx + radius * Math.cos(a)).toFixed(1)},${(cy + radius * Math.sin(a) * 0.35).toFixed(1)}`;
    }).join(' ');
  };

  const topR = hexR * scale;
  const topRotDeg = twist;

  return (
    <svg viewBox="0 0 280 150" width="100%" style={{ display: 'block' }} aria-label="Linear extrude diagram">
      <defs>
        <marker id="arr-le" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0,1 L5,3 L0,5 Z" fill={D.Z_COLOR} />
        </marker>
        <marker id="arr-le-rev" markerWidth="6" markerHeight="6" refX="1" refY="3" orient="auto">
          <path d="M5,1 L0,3 L5,5 Z" fill={D.Z_COLOR} />
        </marker>
      </defs>

      {/* Side lines connecting base corners to top corners (simplified: just left and right tangents) */}
      <line x1={cx - hexR} y1={baseY} x2={cx - topR} y2={topY}
            stroke={D.GEO_STROKE} strokeWidth="1.2" opacity="0.6" />
      <line x1={cx + hexR} y1={baseY} x2={cx + topR} y2={topY}
            stroke={D.GEO_STROKE} strokeWidth="1.2" opacity="0.6" />

      {/* Base polygon */}
      <polygon points={hexPoints(baseY, hexR)}
               fill={D.GEO_FILL} stroke={D.GEO_STROKE} strokeWidth="1.5" />
      <text x={cx} y={baseY + 14} fontSize="9" fontFamily="monospace"
            fill="currentColor" opacity="0.5" textAnchor="middle">2D profile</text>

      {/* Top polygon */}
      <polygon points={hexPoints(topY, topR, topRotDeg)}
               fill="rgba(96,165,250,0.15)" stroke={D.GEO_STROKE} strokeWidth="1.5" />

      {/* Twist spiral line (if twist != 0) */}
      {twist !== 0 && (
        <>
          {Array.from({ length: 8 }, (_, i) => {
            const t = i / 7;
            const y = baseY + (topY - baseY) * t;
            const r = hexR + (topR - hexR) * t;
            const deg = (t * twist) * Math.PI / 180;
            const x = cx + r * Math.cos(deg + Math.PI / 2) * 0.35;
            return i === 0 ? null : (
              <line key={i}
                x1={cx + (hexR + (topR - hexR) * ((i-1)/7)) * Math.cos(((i-1)/7 * twist) * Math.PI / 180 + Math.PI/2) * 0.35}
                y1={baseY + (topY - baseY) * ((i-1)/7)}
                x2={x} y2={y}
                stroke="#f59e0b" strokeWidth="1.2" opacity="0.7" />
            );
          })}
          <text x={cx + topR + 22} y={(topY + baseY) / 2}
                fontSize="9" fontFamily="monospace" fill="#f59e0b" opacity="0.85">
            twist={twist}°
          </text>
        </>
      )}

      {/* Height dimension */}
      <line x1={cx + Math.max(hexR, topR) + 12} y1={baseY}
            x2={cx + Math.max(hexR, topR) + 12} y2={topY}
            stroke={D.Z_COLOR} strokeWidth="1" strokeDasharray={D.DIM_DASH}
            markerEnd="url(#arr-le)" markerStart="url(#arr-le-rev)" />
      <text x={cx + Math.max(hexR, topR) + 22} y={(baseY + topY) / 2 + 4}
            fontSize="10" fontFamily="monospace" fill={D.Z_COLOR} opacity="0.9">
        h={height}
      </text>

      {/* Scale annotation */}
      {scale !== 1 && (
        <text x="8" y="142" fontSize="9" fontFamily="monospace"
              fill={D.X_COLOR} opacity="0.75">scale={scale}  top face is {scale < 1 ? 'smaller' : 'larger'}</text>
      )}
      {scale === 1 && (
        <text x="8" y="142" fontSize="9" fontFamily="monospace"
              fill="currentColor" opacity="0.4">height, twist, scale applied along Z axis</text>
      )}
    </svg>
  );
}
